/**
 * Custom Variant Picker for Apple Watch Bands
 * Apple-Style Minimal UI with Fast Image Updates
 * 
 * Handles three-step guided selection:
 * 1. Gehäusegröße (Case Size)
 * 2. Adapter Farbe (Color) - filtered based on Ultra selection
 * 3. Gelenkumfang (Wrist Size)
 */

if (!customElements.get('variant-picker-custom')) {
  customElements.define(
    'variant-picker-custom',
    class VariantPickerCustom extends HTMLElement {
      constructor() {
        super();
        
        this.variantData = null;
        this.selections = {
          caseSize: null,
          color: null,
          wristSize: null
        };
        
        this.steps = {
          caseSize: null,
          color: null,
          wristSize: null
        };
        
        this.isUpdatingImage = false;
        this.preloadedImages = new Map();
        this.popup = null;
        this.popupContent = null;
        this.sections = ['gehause', 'adapter', 'gelenkumfang'];
        
        // Cached references for performance
        this.formVariantInput = null;
        this.updateDebounceTimer = null;
        this.imageUpdateDebounceTimer = null;

        // Saved fit profile (see fit-profile.js)
        this.fitBanner = null;
        this.fitStrings = {};
        this.fitApplied = null;
        this.fitPrefillActive = false;
        this.fitRevealTimer = null;
        this.fitJourneyStarted = false;
        this.fitCompletedVariants = new Set();
        this.fitGuideUsed = false;
        this.fitSelectionSource = 'default';
      }

      connectedCallback() {
        this.initializeData();
        this.initializeSteps();
        this.initializePopup();
        this.findProductForm();
        this.bindEvents();
        this.initializeFit();
        // Start fetching the colour we are about to restore BEFORE applying it.
        // Otherwise its download only begins once the profile has been applied,
        // by which point the browser has already spent its early bandwidth on
        // the server-rendered default colour — which is exactly the "loads
        // black, then swaps to gold" flash.
        this.preloadVariantImages(this.savedColorDisplayValue());
        this.initializeFromUrl();
        this.restoreWunschmass();
        this.updateScrollIndicators();
        this.interceptFormSubmission();
        
        // Handle browser back/forward cache (bfcache) restoration
        this.handlePageShow = this.handlePageShow.bind(this);
        window.addEventListener('pageshow', this.handlePageShow);
      }

      disconnectedCallback() {
        // Clean up event listeners
        window.removeEventListener('pageshow', this.handlePageShow);
        window.removeEventListener('resize', this.updateScrollIndicators.bind(this));
      }

      handlePageShow(event) {
        // If page is restored from bfcache, reinitialize state
        if (event.persisted) {
          // Reset cached references as they might be stale
          this.formVariantInput = null;
          this.cacheFormVariantInput();
          
          // Clear any stale visual states first
          this.resetAllOptionVisualStates();
          
          // Re-sync selections from current DOM state (radio buttons)
          this.syncSelectionsFromDOM();
          
          // Apply correct color filtering based on case size
          if (this.selections.caseSize) {
            this.filterColorOptions(this.selections.caseSize.includes('Ultra'));
          }
          
          // Update availability display
          if (this.selections.caseSize && this.selections.color) {
            this.updateWristSizeAvailability();
          }
          
          // Re-validate and update form
          if (this.selections.caseSize && this.selections.color && this.selections.wristSize) {
            const variant = this.findMatchingVariant();
            if (variant) {
              this.setVariantId(variant.id, variant);
            }
          }
        }
      }

      syncSelectionsFromDOM() {
        // Read current selections from checked radio buttons
        if (this.steps.caseSize) {
          const checkedCaseSize = this.steps.caseSize.querySelector('.variant-picker-option__input:checked');
          if (checkedCaseSize) {
            this.selections.caseSize = checkedCaseSize.value;
          }
        }
        
        if (this.steps.color) {
          const checkedColor = this.steps.color.querySelector('.variant-picker-option__input:checked');
          if (checkedColor) {
            this.selections.color = checkedColor.value;
          }
        }
        
        if (this.steps.wristSize) {
          const checkedWristSize = this.steps.wristSize.querySelector('.variant-picker-option__input:checked');
          if (checkedWristSize) {
            this.selections.wristSize = checkedWristSize.value;
          }
        }
      }

      initializeData() {
        const dataScript = this.querySelector('[data-variant-data]');
        if (dataScript) {
          try {
            this.variantData = JSON.parse(dataScript.textContent);
            
            // Cache option positions for efficient lookup
            if (this.variantData?.options) {
              this.optionPositions = {
                caseSize: this.variantData.options.caseSize?.position || 1,
                wristSize: this.variantData.options.wristSize?.position || 2,
                color: this.variantData.options.color?.position || 3
              };
            } else {
              // Fallback positions
              this.optionPositions = { caseSize: 1, wristSize: 2, color: 3 };
            }
          } catch (error) {
            console.error('Failed to parse variant data:', error);
            this.optionPositions = { caseSize: 1, wristSize: 2, color: 3 };
          }
        }
      }

      // Get variant option value by semantic name (caseSize, wristSize, color)
      getVariantOption(variant, optionType) {
        if (!variant || !this.optionPositions) return null;
        const position = this.optionPositions[optionType];
        return variant[`option${position}`];
      }

      initializeSteps() {
        this.steps.caseSize = this.querySelector('[data-step="1"]');
        this.steps.color = this.querySelector('[data-step="2"]');
        this.steps.wristSize = this.querySelector('[data-step="3"]');
        Object.values(this.steps).forEach((step) => this.syncStepInputs(step));
      }

      initializePopup() {
        this.popup = this.querySelector('[data-guide-popup]');
        this.popupContent = this.querySelector('[data-guide-content]');
      }

      findProductForm() {
        // Find the product form that this variant picker is associated with
        const productInfo = this.closest('product-info');
        if (productInfo) {
          this.productFormElement = productInfo.querySelector('product-form');
          this.productForm = this.productFormElement?.querySelector('form[data-type="add-to-cart-form"]');
        }
        
        // Fallback: look for form by ID pattern
        if (!this.productForm) {
          const sectionId = this.dataset.section;
          if (sectionId) {
            this.productForm = document.querySelector(`#product-form-${sectionId}`);
            this.productFormElement = this.productForm?.closest('product-form');
          }
        }
        
        // Final fallback: find any product form on the page
        if (!this.productForm) {
          this.productFormElement = document.querySelector('product-form');
          this.productForm = this.productFormElement?.querySelector('form[data-type="add-to-cart-form"]');
        }
        
        // Cache the variant input reference for performance
        this.cacheFormVariantInput();
      }

      cacheFormVariantInput() {
        // Try to find and cache the variant input
        if (this.productForm) {
          this.formVariantInput = this.productForm.querySelector('input[name="id"].product-variant-id') ||
                                   this.productForm.querySelector('input[name="id"]');
        }
        
        if (!this.formVariantInput) {
          const sectionId = this.dataset.section;
          if (sectionId) {
            this.formVariantInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
          }
        }
        
        if (!this.formVariantInput) {
          this.formVariantInput = document.querySelector('product-form form[data-type="add-to-cart-form"] input[name="id"]');
        }
      }

      interceptFormSubmission() {
        if (!this.productFormElement) return;
        
        // Store reference to original submit handler
        const form = this.productForm;
        if (!form) return;

        // Add our validation before form submission
        form.addEventListener('submit', (e) => {
          if (!this.validateSelections()) {
            this.trackFit('add_to_cart_attempted', {});
            this.trackFit('add_to_cart_failed', {
              failure_reason: 'incomplete_selection',
              missing_fields: Object.keys(this.selections).filter(key => !this.selections[key]).join(',')
            });
            e.preventDefault();
            e.stopPropagation();
            this.showWarning();
            return false;
          }
          this.hideWarning();

          // Adding to cart is the strongest signal we get that this really is
          // the shopper's watch and wrist — promote the profile to "confirmed",
          // which is what lets the banner say "wie zuletzt bestellt".
          this.saveFit(
            {
              case: this.currentCanonicalKey(this.steps.caseSize),
              color: this.currentCanonicalKey(this.steps.color),
              wrist: this.currentCanonicalKey(this.steps.wristSize)
            },
            { confirm: true, source: 'cart' }
          );
        }, true); // Use capture phase to run before other handlers
      }

      validateSelections() {
        return this.selections.caseSize && this.selections.color && this.selections.wristSize;
      }

      /** The option's visible, localised name: the legend's own text before the "-" separator. */
      stepName(step) {
        if (!step) return '';
        const legend = step.querySelector('.variant-picker-step__label');
        let text = '';
        legend?.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) text += node.textContent;
        });
        return text.trim() || step.dataset.optionName || '';
      }

      /** "Bitte wähle noch Gelenkumfang aus." — only what is actually missing. */
      warningMessage() {
        const missing = [];
        if (!this.selections.caseSize) missing.push(this.stepName(this.steps.caseSize));
        if (!this.selections.color) missing.push(this.stepName(this.steps.color));
        if (!this.selections.wristSize) missing.push(this.stepName(this.steps.wristSize));
        const names = missing.filter(Boolean);
        const allText = this.dataset.warningSelectAll
          || 'Bitte wähle alle Optionen aus: Gehäusegröße, Adapter Farbe und Gelenkumfang.';
        const template = this.dataset.warningSelectMissing || 'Bitte wähle noch %s aus.';
        if (names.length === 0 || names.length === 3 || !template.includes('%s')) return allText;
        const join = this.dataset.warningJoin || ' und ';
        return template.replace('%s', names.join(join));
      }

      showWarning() {
        // Create or show warning element
        let warning = this.querySelector('.variant-picker-warning');
        if (!warning) {
          warning = document.createElement('div');
          warning.className = 'variant-picker-warning';
          // Announced on every submit attempt, not only once it is created.
          warning.setAttribute('role', 'alert');
          const iconSpan = document.createElement('span');
          iconSpan.className = 'variant-picker-warning__icon';
          iconSpan.textContent = '⚠';
          iconSpan.setAttribute('aria-hidden', 'true');
          const textSpan = document.createElement('span');
          textSpan.className = 'variant-picker-warning__text';
          warning.append(iconSpan, textSpan);
          // Insert before the help box or at the end of the picker
          const helpBox = this.querySelector('.variant-picker-help-box');
          if (helpBox) {
            helpBox.parentNode.insertBefore(warning, helpBox);
          } else {
            this.appendChild(warning);
          }
        }
        const textSpan = warning.querySelector('.variant-picker-warning__text');
        if (textSpan) textSpan.textContent = this.warningMessage();
        warning.classList.add('variant-picker-warning--visible');
        
        // Scroll warning into view
        warning.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Highlight missing steps
        this.highlightMissingSteps();
      }

      hideWarning() {
        const warning = this.querySelector('.variant-picker-warning');
        if (warning) {
          warning.classList.remove('variant-picker-warning--visible');
        }
        // Remove highlights
        this.querySelectorAll('.variant-picker-step--highlight-missing').forEach(step => {
          step.classList.remove('variant-picker-step--highlight-missing');
        });
      }

      highlightMissingSteps() {
        if (!this.selections.caseSize && this.steps.caseSize) {
          this.steps.caseSize.classList.add('variant-picker-step--highlight-missing');
        }
        if (!this.selections.color && this.steps.color) {
          this.steps.color.classList.add('variant-picker-step--highlight-missing');
        }
        if (!this.selections.wristSize && this.steps.wristSize) {
          this.steps.wristSize.classList.add('variant-picker-step--highlight-missing');
        }
      }

      // ============================================================
      // Image Preloading for Speed
      // ============================================================

      /** The `sizes` the live product image uses — one source of truth. */
      imageSizesAttr() {
        const img = this.findMainProductImage();
        return (img && img.getAttribute('sizes')) || '(min-width: 990px) 50vw, 100vw';
      }

      /**
       * Warm the cache with one image per adapter colour (compositions are
       * identical across wrist sizes, so colour is the only axis that matters).
       *
       * Two things have to be right or this whole function is wasted work:
       *
       *  - It must fetch the EXACT candidate the <img> will be asked to show.
       *    The display path sets a srcset, so the browser picks by width;
       *    warming a width that srcset never requests downloads a file nobody
       *    displays AND leaves the real one uncached. Passing the same
       *    srcset/sizes here lets the browser pick the identical candidate.
       *
       *  - With a restored profile the shopper is looking at exactly ONE
       *    colour. Fetching the other seven first only delays the one that is
       *    actually on screen, so the saved colour jumps the queue and the rest
       *    trickle in behind it rather than competing for bandwidth.
       */
      preloadVariantImages(priorityColorValue) {
        if (!this.variantData?.variants) return;

        const byColor = new Map();
        for (const variant of this.variantData.variants) {
          const color = this.getVariantOption(variant, 'color');
          const imageData = variant.featured_image || variant.image;
          if (color && imageData?.src && !byColor.has(color)) {
            byColor.set(color, imageData.src);
          }
        }

        const queue = [];
        if (priorityColorValue && byColor.has(priorityColorValue)) {
          queue.push(byColor.get(priorityColorValue));
        }
        byColor.forEach((src) => {
          if (queue.indexOf(src) === -1) queue.push(src);
        });
        if (!queue.length) return;

        const sizes = this.imageSizesAttr();
        const warm = (src) => {
          const img = new Image();
          img.sizes = sizes;
          img.srcset = this.generateSrcset(src);
          img.onload = () => this.preloadedImages.set(src, true);
          img.src = this.getSizedImageUrl(src, 600); // fallback for no-srcset UAs
        };

        // A colour restored from a saved profile is about to be shown: fetch it now.
        const restoring = priorityColorValue && byColor.has(priorityColorValue);
        if (restoring) warm(queue[0]);

        // Perf: the other finishes (~8 photos, ~100 KB each) wait until the
        // shopper shows interest in the picker — hovering it, touching it or
        // tabbing into it. Most product views never change the finish, and
        // warming all of them on every visit cost ~0.8 MB of mobile data.
        const rest = restoring ? queue.slice(1) : queue;
        if (!rest.length) return;
        let started = false;
        const start = () => {
          if (started) return;
          started = true;
          const step = (i) => {
            if (i >= rest.length) return;
            for (let n = 0; n < 3 && i + n < rest.length; n++) warm(rest[i + n]);
            setTimeout(() => step(i + 3), 120);
          };
          step(0);
        };
        ['pointerenter', 'pointerdown', 'touchstart', 'focusin'].forEach((type) =>
          this.addEventListener(type, start, { once: true, passive: true }));
      }

      // ============================================================
      // Event Binding
      // ============================================================

      bindEvents() {
        this.addEventListener('click', (event) => {
          const option = event.target.closest('.variant-picker-option--unavailable, .variant-picker-option--nonexistent');
          if (!option) return;
          this.trackFit('fit_blocked', {
            failure_reason: option.classList.contains('variant-picker-option--nonexistent') ? 'combination_not_offered' : 'out_of_stock',
            fit_value: option.dataset.canonicalKey || ''
          });
        });
        // Case size selection
        if (this.steps.caseSize) {
          this.steps.caseSize.querySelectorAll('.variant-picker-option__input').forEach(input => {
            input.addEventListener('change', this.handleCaseSizeChange.bind(this));
          });
        }

        // Color selection
        if (this.steps.color) {
          this.steps.color.querySelectorAll('.variant-picker-option__input').forEach(input => {
            input.addEventListener('change', this.handleColorChange.bind(this));
          });
        }

        // Wrist size selection
        if (this.steps.wristSize) {
          this.steps.wristSize.querySelectorAll('.variant-picker-option__input').forEach(input => {
            input.addEventListener('change', this.handleWristSizeChange.bind(this));
          });
          
          const scrollContainer = this.steps.wristSize.querySelector('.variant-picker-step__scroll-container');
          if (scrollContainer) {
            scrollContainer.addEventListener('scroll', this.updateScrollIndicators.bind(this));
            window.addEventListener('resize', this.updateScrollIndicators.bind(this));
          }
        }

        // Help Box link
        const helpLink = this.querySelector('[data-open-guide]');
        if (helpLink) {
          helpLink.addEventListener('click', this.openPopup.bind(this));
        }

        // Popup close controls (✕ button and the "Done" footer button)
        this.querySelectorAll('[data-guide-close]').forEach(btn => {
          btn.addEventListener('click', this.closePopup.bind(this));
        });

        // Close on backdrop click
        if (this.popup) {
          this.popup.addEventListener('click', (e) => {
            if (e.target === this.popup) this.closePopup();
          });
        }

        // Navigation buttons - scroll to section
        this.querySelectorAll('[data-guide-nav]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            const sectionType = e.currentTarget.dataset.guideNav;
            this.scrollToSection(sectionType);
          });
        });

        // Keyboard navigation
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
      }

      // ============================================================
      // Guide Popup with Scrollable Sections
      // ============================================================

      openPopup(event) {
        event.preventDefault();
        
        if (this.popup) {
          this.fitGuideUsed = true;
          this.trackFit('size_guide_opened', { guide: 'band_fit' });
          this.popup.classList.add('variant-picker-guide-popup--open');
          document.body.style.overflow = 'hidden';
          
          // Scroll content to top
          if (this.popupContent) {
            this.popupContent.scrollTop = 0;
          }
        }
      }

      closePopup() {
        if (this.popup) {
          if (this.popup.classList.contains('variant-picker-guide-popup--open')) {
            this.trackFit('size_guide_closed', { guide: 'band_fit' });
          }
          this.popup.classList.remove('variant-picker-guide-popup--open');
          document.body.style.overflow = '';
        }
      }

      scrollToSection(sectionType) {
        const section = this.querySelector(`[data-guide-section="${sectionType}"]`);
        const content = this.popupContent;
        
        if (section && content) {
          // Get the section's position relative to the scrollable content
          const sectionTop = section.offsetTop - content.offsetTop;
          
          // Smooth scroll to the section
          content.scrollTo({
            top: sectionTop - 10,
            behavior: 'smooth'
          });
          
          // Update active nav button
          this.querySelectorAll('[data-guide-nav]').forEach(btn => {
            btn.classList.toggle('variant-picker-guide-nav-btn--active', btn.dataset.guideNav === sectionType);
          });
        }
      }

      handleKeyDown(event) {
        if (!this.popup?.classList.contains('variant-picker-guide-popup--open')) return;
        
        if (event.key === 'Escape') {
          this.closePopup();
        }
      }

      // ============================================================
      // Saved Fit Profile
      //
      // Every band shares identical option values, so a configuration the
      // shopper made once transfers to every other band. We restore it, but we
      // always *show* that we did — a prefill that looks like a default is how
      // someone ends up ordering the wrong wrist size.
      // ============================================================

      initializeFit() {
        if (!window.ZelalFit) return;

        this.fitBanner = this.querySelector('[data-fit-banner]');

        const stringsEl = this.querySelector('[data-fit-strings]');
        if (stringsEl) {
          try {
            this.fitStrings = JSON.parse(stringsEl.textContent);
          } catch (error) {
            this.fitStrings = {};
          }
        }

        // Carry a profile onto this device from the shopper's last order.
        const seedEl = this.querySelector('[data-fit-seed]');
        if (seedEl) {
          try {
            window.ZelalFit.seedFromOrder(JSON.parse(seedEl.textContent));
          } catch (error) {
            /* a malformed seed simply means no cross-device restore */
          }
        }

        const action = this.fitBanner?.querySelector('[data-fit-action]');
        if (action) {
          action.addEventListener('click', this.handleFitAction.bind(this));
        }
      }

      /**
       * The display value of the colour a saved profile is about to restore,
       * resolved before the picker touches the DOM so its image can start
       * downloading in the same tick the element upgrades.
       */
      savedColorDisplayValue() {
        if (!window.ZelalFit || !this.isBandProduct()) return null;
        const profile = window.ZelalFit.read();
        if (!profile || !profile.case) return null;
        const el = this.findOptionByKey(
          this.steps.color,
          window.ZelalFit.colorForCase(profile.color, profile.case)
        );
        return el ? el.dataset.optionValue : null;
      }

      /** Is this a three-step band product? Gift cards etc. render the shell only. */
      isBandProduct() {
        return !!(this.steps.caseSize && this.steps.color && this.steps.wristSize);
      }

      findOptionByKey(step, key) {
        if (!step || !key) return null;
        return step.querySelector(
          `.variant-picker-option[data-canonical-key="${CSS.escape(key)}"]`
        );
      }

      currentCanonicalKey(step) {
        const selected = step?.querySelector('.variant-picker-option--selected');
        return selected?.dataset.canonicalKey || null;
      }

      optionLabelText(optionEl) {
        return optionEl?.querySelector('.variant-picker-option__label')?.textContent.trim() || '';
      }

      /**
       * Restore a stored configuration onto this product.
       * Returns true when it took over the initial selection.
       */
      applySavedProfile() {
        if (!window.ZelalFit || !this.isBandProduct() || !this.variantData) return false;

        const profile = window.ZelalFit.read();
        if (!profile || !profile.case) return false;

        const caseEl = this.findOptionByKey(this.steps.caseSize, profile.case);
        if (!caseEl) {
          this.trackFit('fit_prefilled', { fit_complete: 'no', failure_reason: 'case_unavailable' });
          return false;
        }
        this.fitSelectionSource = 'saved_fit';

        // Clear the server-rendered defaults first — both the DOM and our own
        // state. Liquid pre-checks the first available variant's values on every
        // step, so without this the picker would keep showing a colour we never
        // selected, and a leftover wrist size could complete a variant the
        // shopper never actually chose.
        this.selections = { caseSize: null, color: null, wristSize: null };
        this.resetStep(this.steps.caseSize);
        this.resetStep(this.steps.color);
        this.resetStep(this.steps.wristSize);

        const caseValue = caseEl.dataset.optionValue;
        this.selections.caseSize = caseValue;
        this.selectOption(this.steps.caseSize, caseValue);
        this.enableStep(this.steps.color);
        this.filterColorOptions(profile.case === 'ultra');

        // An Ultra case only takes two finishes, so translate the stored colour
        // rather than dropping it.
        let colorEl = null;
        const colorKey = window.ZelalFit.colorForCase(profile.color, profile.case);
        const candidate = this.findOptionByKey(this.steps.color, colorKey);
        if (candidate && candidate.dataset.hidden !== 'true') {
          colorEl = candidate;
          this.selections.color = candidate.dataset.optionValue;
          this.selectOption(this.steps.color, this.selections.color);
          this.enableStep(this.steps.wristSize);
          this.updateWristSizeAvailability();
        }

        // Only take the saved wrist size if it genuinely exists and is in stock
        // for THIS band. Silently sliding someone to the next size up is how you
        // generate a return.
        let wristEl = null;
        let unavailableWrist = null;
        if (colorEl) {
          const candidateWrist = this.findOptionByKey(this.steps.wristSize, profile.wrist);
          if (candidateWrist) {
            const usable =
              !candidateWrist.classList.contains('variant-picker-option--unavailable') &&
              !candidateWrist.classList.contains('variant-picker-option--nonexistent');

            if (usable) {
              wristEl = candidateWrist;
              this.selections.wristSize = candidateWrist.dataset.optionValue;
              this.selectOption(this.steps.wristSize, this.selections.wristSize);
            } else {
              unavailableWrist = this.optionLabelText(candidateWrist) || profile.wrist.toUpperCase();
            }
          }
        }

        this.updateSelectedLabels();

        if (this.selections.caseSize && this.selections.color && this.selections.wristSize) {
          const variant = this.findMatchingVariant();
          if (variant) this.setVariantId(variant.id, variant);
        } else {
          this.clearVariantId();
        }

        // The adapter finish changes the product photo, so bring the gallery in
        // line with what we just selected.
        this.updateVariantImage();

        this.fitApplied = {
          case: profile.case,
          color: colorEl ? colorEl.dataset.canonicalKey : null,
          wrist: wristEl ? wristEl.dataset.canonicalKey : null
        };
        this.fitPrefillActive = true;

        this.revealFitPrefill();
        this.showFitBanner({
          caseLabel: this.optionLabelText(caseEl),
          colorLabel: colorEl ? colorEl.dataset.optionValue : null,
          wristLabel: wristEl ? this.optionLabelText(wristEl) : null,
          fromOrder: profile.source === 'order',
          unavailableWrist
        });

        // Every param is `fit_`-prefixed on purpose: GA4 already has built-in
        // dimensions called Source / Field, and an unprefixed custom one sits
        // next to them in every report picker looking identical. The prefix also
        // makes the whole feature's dimensions findable as a group.
        this.trackFit('fit_prefilled', {
          fit_source: profile.source || 'selection',
          fit_case: profile.case,
          fit_color: this.fitApplied.color || '',
          fit_wrist: this.fitApplied.wrist || '',
          fit_complete: this.fitApplied.wrist ? 'yes' : 'no',
          failure_reason: this.prefillFailureReason(profile, colorEl, wristEl)
        });
        this.trackFitCompletion();

        return true;
      }

      /**
       * Stagger the restored pills into place instead of having them appear
       * already-chosen. The shopper watches the form get filled in, which reads
       * as something done for them rather than as a default they might miss.
       * The animation itself lives in CSS (and is disabled under
       * prefers-reduced-motion); we only flag the window in which it runs.
       */
      revealFitPrefill() {
        this.classList.add('variant-picker-custom--fit-prefilled');
        clearTimeout(this.fitRevealTimer);
        this.fitRevealTimer = setTimeout(() => {
          this.classList.remove('variant-picker-custom--fit-prefilled');
        }, 900);
      }

      showFitBanner({ caseLabel, colorLabel, wristLabel, fromOrder, unavailableWrist }) {
        if (!this.fitBanner) return;

        const parts = [];
        if (caseLabel) parts.push(`${caseLabel} mm`);
        if (colorLabel) parts.push(colorLabel);
        if (wristLabel) parts.push(wristLabel);

        const summary = this.fitBanner.querySelector('[data-fit-summary]');
        const reason = this.fitBanner.querySelector('[data-fit-reason]');
        const note = this.fitBanner.querySelector('[data-fit-note]');

        if (summary) summary.textContent = parts.join(' · ');
        if (reason) {
          reason.textContent = fromOrder
            ? this.fitStrings.reasonOrder || ''
            : this.fitStrings.reasonProfile || '';
        }

        if (note) {
          if (unavailableWrist && this.fitStrings.unavailable) {
            note.textContent = this.fitStrings.unavailable.replace('%s', unavailableWrist);
            note.hidden = false;
          } else {
            note.textContent = '';
            note.hidden = true;
          }
        }

        this.fitBanner.classList.add('fit-restore--visible');
      }

      setFitBannerMessage(message, nextAction) {
        if (!this.fitBanner) return;

        const summary = this.fitBanner.querySelector('[data-fit-summary]');
        const reason = this.fitBanner.querySelector('[data-fit-reason]');
        const note = this.fitBanner.querySelector('[data-fit-note]');
        const action = this.fitBanner.querySelector('[data-fit-action]');

        if (summary) summary.textContent = '';
        if (reason) reason.textContent = message || '';
        if (note) {
          note.textContent = '';
          note.hidden = true;
        }

        if (action) {
          if (nextAction) {
            action.dataset.fitAction = nextAction;
            action.textContent = this.fitStrings[nextAction] || '';
            action.hidden = false;
          } else {
            action.hidden = true;
          }
        }
      }

      collapseFitBanner() {
        this.fitBanner?.classList.remove('fit-restore--visible');
      }

      handleFitAction(event) {
        const action = event.currentTarget.dataset.fitAction;

        if (action === 'forget') {
          window.ZelalFit?.disable();
          this.fitPrefillActive = false;
          this.setFitBannerMessage(this.fitStrings.didForget, null);
          this.trackFit('fit_forgotten', {});
          setTimeout(() => this.collapseFitBanner(), 2600);
          return;
        }

        // "Reset" clears this page's prefill but keeps the stored profile —
        // someone correcting one band hasn't necessarily changed their watch.
        // The permanent opt-out is offered right after, where they're looking.
        this.clearAllSelections();
        this.fitPrefillActive = false;
        this.setFitBannerMessage(this.fitStrings.didReset, 'forget');
        this.trackFit('fit_reset', {});
      }

      clearAllSelections() {
        this.selections = { caseSize: null, color: null, wristSize: null };
        this.resetStep(this.steps.caseSize);
        this.resetStep(this.steps.color);
        this.resetStep(this.steps.wristSize);
        this.disableStep(this.steps.color);
        this.disableStep(this.steps.wristSize);
        this.resetAllOptionVisualStates();
        this.updateSelectedLabels();
        this.clearVariantId();
      }

      saveFit(patch, options) {
        if (!window.ZelalFit || !patch) return;
        // "Wunschmaß" is a theme-only size, not a wrist size: it must never replace the
        // shopper's saved size, which every other band restores from.
        if (patch.wrist === 'wunschmass') {
          patch = Object.assign({}, patch);
          delete patch.wrist;
        }
        if (!patch.case && !patch.color && !patch.wrist && !(options && options.confirm)) return;
        window.ZelalFit.write(patch, options);
      }

      // ============================================================
      // Made-to-measure (Wunschmaß) reload memory
      // ============================================================

      wunschmassOption() {
        return this.steps.wristSize?.querySelector('[data-canonical-key="wunschmass"]') || null;
      }

      isWunschmassSelected() {
        const option = this.wunschmassOption();
        return !!option && !!this.selections.wristSize && this.selections.wristSize === option.dataset.optionValue;
      }

      wunschmassStoreKey() {
        return 'zelal:wunschmass:' + (this.dataset.productUrl || window.location.pathname);
      }

      /**
       * Wunschmaß has no variant and therefore no ?variant= link; this tab remembers the
       * choice (and the inline card script the typed cm value) under the same session key,
       * so a reload comes back as Wunschmaß instead of a plain M band.
       */
      rememberWunschmass(selected) {
        try {
          const key = this.wunschmassStoreKey();
          if (!selected) {
            window.sessionStorage.removeItem(key);
            return;
          }
          const cur = JSON.parse(window.sessionStorage.getItem(key) || 'null') || {};
          cur.selected = true;
          window.sessionStorage.setItem(key, JSON.stringify(cur));
        } catch (e) {
          /* storage blocked: no reload memory */
        }
      }

      restoreWunschmass() {
        let stored = null;
        try {
          stored = JSON.parse(window.sessionStorage.getItem(this.wunschmassStoreKey()) || 'null');
        } catch (e) {
          return;
        }
        if (!stored || !stored.selected) return;
        const option = this.wunschmassOption();
        const input = option?.querySelector('.variant-picker-option__input');
        // Only when the size step is reachable (case + colour chosen) and the base size is sold.
        if (!input || !this.selections.caseSize || !this.selections.color) return;
        if (option.classList.contains('variant-picker-option--unavailable') ||
            option.classList.contains('variant-picker-option--nonexistent')) return;
        if (input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Called whenever the shopper picks something themselves. The prefill is
       * consumed for this page view — we never re-apply on top of a deliberate
       * choice — and an actual correction gets reported.
       */
      onManualInput(field, newKey) {
        if (!this.fitJourneyStarted) {
          this.fitJourneyStarted = true;
          this.trackFit('fit_started', { selection_source: this.fitSelectionSource });
        }
        this.fitSelectionSource = 'manual';
        this.trackFit('fit_step_completed', { fit_field: field, fit_value: newKey });
        if (!this.fitPrefillActive) return;

        const previous = this.fitApplied?.[field];
        if (previous && newKey && previous !== newKey) {
          this.trackFit('fit_overridden', { fit_field: field, fit_from: previous, fit_to: newKey });
        }

        this.fitPrefillActive = false;
        this.collapseFitBanner();
      }

      canonicalKeyFor(input) {
        return input?.closest('.variant-picker-option')?.dataset.canonicalKey || null;
      }

      trackFit(name, data) {
        // Sandboxed pixels only receive Shopify's standard events, so in-page
        // interactions have to go out on the bus and be picked up by the custom
        // pixel. Analytics must never be able to break the picker.
        try {
          if (window.Shopify?.analytics?.publish) {
            const result = window.Shopify.analytics.publish(`zelal:${name}`, {
              product_id: this.closest('product-info')?.dataset.productId,
              product_family: 'band', surface: 'product',
              guide_used: this.fitGuideUsed, selection_source: this.fitSelectionSource,
              ...data
            });
            if (result?.catch) result.catch(() => {});
          }
        } catch (error) {
          /* no-op */
        }
      }

      // ============================================================
      // Selection Handlers
      // ============================================================

      prefillFailureReason(profile, colorEl, wristEl) {
        if (wristEl) return 'none';
        if (!profile.color) return 'missing_saved_colour';
        if (!colorEl) return 'colour_unavailable';
        if (!profile.wrist) return 'missing_saved_size';
        const wrist = this.findOptionByKey(this.steps.wristSize, profile.wrist);
        if (!wrist || wrist.classList.contains('variant-picker-option--nonexistent')) return 'combination_not_offered';
        return 'out_of_stock';
      }

      trackFitCompletion() {
        if (!this.validateSelections()) return;
        const variant = this.findMatchingVariant();
        if (!variant?.available) {
          this.trackFit('fit_blocked', { failure_reason: variant ? 'out_of_stock' : 'combination_not_offered' });
          return;
        }
        // Defaults alone are not evidence of a shopper choosing a fit.
        if (this.fitSelectionSource === 'default' || this.fitCompletedVariants.has(variant.id)) return;
        this.fitCompletedVariants.add(variant.id);
        this.trackFit('fit_completed', { variant_id: String(variant.id) });
      }

      initializeFromUrl() {
        // Clear any stale visual states first
        this.resetAllOptionVisualStates();
        
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        
        if (variantId && this.variantData) {
          const variant = this.variantData.variants.find(v => v.id.toString() === variantId);
          if (variant) {
            this.fitSelectionSource = 'variant_link';
            this.setSelectionsFromVariant(variant);
            this.trackFitCompletion();
            return;
          }
        }

        // A saved fit profile outranks the server-rendered default but never a
        // shared ?variant= link. This has to sit ABOVE the DOM-sync fallback:
        // Liquid pre-checks selected_or_first_available_variant on every step,
        // so the DOM always looks like it "has selections" and would otherwise
        // win every time.
        if (this.applySavedProfile()) return;

        // Fallback: check if DOM already has selections (from server render or bfcache)
        this.syncSelectionsFromDOM();
        if (this.selections.caseSize) {
          // DOM had selections, validate and use them
          this.enableStep(this.steps.color);
          this.filterColorOptions(this.selections.caseSize.includes('Ultra'));
          
          if (this.selections.color) {
            this.enableStep(this.steps.wristSize);
            this.updateWristSizeAvailability();
          }
          
          if (this.selections.caseSize && this.selections.color && this.selections.wristSize) {
            const variant = this.findMatchingVariant();
            if (variant) {
              this.setVariantId(variant.id, variant);
            }
          }
          return;
        }
        
        // Final fallback: use Liquid-provided selectedVariant
        if (this.variantData?.selectedVariant) {
          this.setSelectionsFromVariant(this.variantData.selectedVariant);
        }
      }

      setSelectionsFromVariant(variant) {
        if (!variant) return;
        
        // Reset all visual states first to ensure clean state
        this.resetAllOptionVisualStates();
        
        const caseSize = this.getVariantOption(variant, 'caseSize');
        const color = this.getVariantOption(variant, 'color');
        const wristSize = this.getVariantOption(variant, 'wristSize');
        
        if (caseSize) {
          this.selections.caseSize = caseSize;
          this.selectOption(this.steps.caseSize, caseSize);
          this.enableStep(this.steps.color);
          this.filterColorOptions(caseSize.includes('Ultra'));
        }
        
        if (color) {
          this.selections.color = color;
          this.selectOption(this.steps.color, color);
          this.enableStep(this.steps.wristSize);
        }
        
        if (wristSize) {
          this.selections.wristSize = wristSize;
          this.selectOption(this.steps.wristSize, wristSize);
        }
        
        this.updateSelectedLabels();
        
        // Update the variant ID in the form if all selections are made
        if (this.selections.caseSize && this.selections.color && this.selections.wristSize) {
          this.setVariantId(variant.id, variant);
        }
        
        // Update availability display for wrist size options
        this.updateWristSizeAvailability();
      }

      resetAllOptionVisualStates() {
        // Clear unavailable/nonexistent classes from all options
        this.querySelectorAll('.variant-picker-option').forEach(option => {
          option.classList.remove('variant-picker-option--unavailable');
          option.classList.remove('variant-picker-option--nonexistent');
        });
      }

      updateWristSizeAvailability() {
        // Show which wrist sizes are available for current case+color selection
        if (!this.steps.wristSize || !this.variantData || !this.selections.caseSize || !this.selections.color) return;
        
        const normalizedCaseSize = this.normalizeOptionValue(this.selections.caseSize);
        const normalizedColor = this.normalizeOptionValue(this.selections.color);
        
        this.steps.wristSize.querySelectorAll('.variant-picker-option').forEach(option => {
          const wristSize = this.normalizeOptionValue(this.wristMatchValue(option.dataset.optionValue));
          
          // Find if this variant exists and is available
          const variant = this.variantData.variants.find(v => 
            this.normalizeOptionValue(this.getVariantOption(v, 'caseSize')) === normalizedCaseSize && 
            this.normalizeOptionValue(this.getVariantOption(v, 'wristSize')) === wristSize && 
            this.normalizeOptionValue(this.getVariantOption(v, 'color')) === normalizedColor
          );
          
          const isAvailable = variant?.available ?? false;
          const variantExists = !!variant;
          
          // Update visual state
          option.classList.toggle('variant-picker-option--unavailable', !isAvailable && variantExists);
          option.classList.toggle('variant-picker-option--nonexistent', !variantExists);
          
          // Update the input
          const input = option.querySelector('input');
          if (input) {
            // Don't disable - let users select unavailable items to see "sold out" message
            input.dataset.available = isAvailable.toString();
          }
        });
      }

      selectOption(step, value) {
        if (!step) return;
        
        const input = step.querySelector(`input[value="${CSS.escape(value)}"]`);
        if (input) {
          input.checked = true;
          this.updateOptionStyles(step, value);
        }
      }

      updateOptionStyles(step, selectedValue) {
        step.querySelectorAll('.variant-picker-option').forEach(option => {
          const isSelected = option.dataset.optionValue === selectedValue;
          const wasSelected = option.classList.contains('variant-picker-option--selected');
          
          option.classList.toggle('variant-picker-option--selected', isSelected);
          
          // Add pulse animation on new selection
          if (isSelected && !wasSelected) {
            option.classList.add('variant-picker-option--just-selected');
            setTimeout(() => {
              option.classList.remove('variant-picker-option--just-selected');
            }, 250);
          }
        });
      }

      handleCaseSizeChange(event) {
        const value = event.target.value;
        const canonicalKey = this.canonicalKeyFor(event.target);
        this.onManualInput('case', canonicalKey);
        this.saveFit({ case: canonicalKey });

        this.selections.caseSize = value;

        this.selections.color = null;
        this.selections.wristSize = null;
        this.resetStep(this.steps.color);
        this.resetStep(this.steps.wristSize);
        this.disableStep(this.steps.wristSize);
        
        this.updateOptionStyles(this.steps.caseSize, value);
        
        const isUltra = value.includes('Ultra');
        this.enableStep(this.steps.color);
        this.filterColorOptions(isUltra);
        
        this.updateSelectedLabels();
        this.clearVariantId();
        this.updateVariantImage();
      }

      handleColorChange(event) {
        const value = event.target.value;
        const canonicalKey = this.canonicalKeyFor(event.target);
        this.onManualInput('color', canonicalKey);
        this.saveFit({ color: canonicalKey });

        this.selections.color = value;

        this.selections.wristSize = null;
        this.resetStep(this.steps.wristSize);
        
        this.updateOptionStyles(this.steps.color, value);
        this.enableStep(this.steps.wristSize);
        
        // Update wrist size availability after color selection
        this.updateWristSizeAvailability();
        
        this.updateSelectedLabels();
        this.clearVariantId();
        this.updateVariantImage();
      }

      handleWristSizeChange(event) {
        const value = event.target.value;
        const canonicalKey = this.canonicalKeyFor(event.target);
        this.onManualInput('wrist', canonicalKey);
        this.saveFit({ wrist: canonicalKey });
        this.rememberWunschmass(canonicalKey === 'wunschmass');

        this.selections.wristSize = value;

        this.updateOptionStyles(this.steps.wristSize, value);
        this.updateSelectedLabels();
        this.updateVariant();
        this.trackFitCompletion();
      }

      filterColorOptions(isUltra) {
        if (!this.steps.color || !this.variantData) return;

        const colorMapping = this.variantData.colorMapping;
        const allowedColors = isUltra ? colorMapping.ultra : colorMapping.standard;

        this.steps.color.querySelectorAll('.variant-picker-option--color').forEach(option => {
          // Group by the locale-invariant canonical key, not the translated display value,
          // so colorMapping (stored in the stable German keys) matches on every storefront.
          const colorKey = option.dataset.colorKey || option.dataset.optionValue;
          const isAllowed = allowedColors.includes(colorKey);
          option.dataset.hidden = (!isAllowed).toString();
          option.style.display = isAllowed ? '' : 'none';
        });
        this.syncStepInputs(this.steps.color);
      }

      /**
       * Keep the radios' focusability in line with what the shopper can see.
       * A greyed-out step and a colour hidden for the current case must not be
       * reachable with Tab or the arrow keys: otherwise the Tab order skips a
       * radio group whose first radio is hidden (Ultra: Schwarz), and a wrist
       * size can be checked before a colour exists.
       */
      syncStepInputs(step) {
        if (!step) return;
        const stepDisabled = step.classList.contains('variant-picker-step--disabled');
        step.querySelectorAll('.variant-picker-option').forEach((option) => {
          const input = option.querySelector('.variant-picker-option__input');
          if (!input) return;
          input.disabled = stepDisabled || option.dataset.hidden === 'true';
        });
      }

      enableStep(step) {
        if (!step) return;
        
        step.classList.remove('variant-picker-step--disabled');
        step.classList.add('variant-picker-step--just-enabled');
        this.syncStepInputs(step);
        
        setTimeout(() => {
          step.classList.remove('variant-picker-step--just-enabled');
        }, 300);
      }

      disableStep(step) {
        if (!step) return;
        step.classList.add('variant-picker-step--disabled');
        this.syncStepInputs(step);
      }

      resetStep(step) {
        if (!step) return;
        
        step.querySelectorAll('.variant-picker-option__input').forEach(input => {
          input.checked = false;
        });
        
        step.querySelectorAll('.variant-picker-option').forEach(option => {
          option.classList.remove('variant-picker-option--selected');
        });
        
        const selectedLabel = step.querySelector('[data-selected-value]');
        if (selectedLabel) {
          selectedLabel.textContent = '';
        }
      }

      updateSelectedLabels() {
        Object.entries(this.steps).forEach(([key, step]) => {
          if (!step) return;
          
          const label = step.querySelector('[data-selected-value]');
          if (label) {
            const value = this.selections[key];
            label.textContent = value || '';
          }
        });
      }

      // ============================================================
      // Fast Image Update Logic
      // ============================================================

      getDefaultColor() {
        // defaultColors are stored as locale-invariant canonical keys. Resolve the key to the
        // colour option's DISPLAY value in the DOM, because downstream variant lookups compare
        // against variant.optionN (the translated value on a translated storefront).
        const resolveKeyToDisplay = (key) => {
          if (this.steps.color && key) {
            const match = this.steps.color.querySelector(
              `.variant-picker-option--color[data-color-key="${CSS.escape(key)}"]`
            );
            if (match) return match.dataset.optionValue;
          }
          return null;
        };

        const isUltra = this.selections.caseSize?.includes('Ultra');
        const defaultKey = isUltra
          ? this.variantData?.defaultColors?.ultra
          : this.variantData?.defaultColors?.standard;

        return (
          resolveKeyToDisplay(defaultKey) ||
          // Fallback: first colour option's display value
          this.steps.color?.querySelector('.variant-picker-option--color')?.dataset.optionValue ||
          null
        );
      }

      getFirstWristSize() {
        if (!this.variantData) return null;
        
        const normalizedCaseSize = this.normalizeOptionValue(this.selections.caseSize);
        const normalizedColor = this.normalizeOptionValue(this.selections.color || this.getDefaultColor());
        
        const matchingVariant = this.variantData.variants.find(v => 
          this.normalizeOptionValue(this.getVariantOption(v, 'caseSize')) === normalizedCaseSize && 
          this.normalizeOptionValue(this.getVariantOption(v, 'color')) === normalizedColor
        );
        
        return this.getVariantOption(matchingVariant, 'wristSize') || null;
      }

      findVariantForImage() {
        if (!this.variantData || !this.selections.caseSize) return null;
        
        const caseSize = this.normalizeOptionValue(this.selections.caseSize);
        const color = this.normalizeOptionValue(this.selections.color || this.getDefaultColor());
        const wristSize = this.normalizeOptionValue(this.wristMatchValue(this.selections.wristSize) || this.getFirstWristSize());
        
        if (!wristSize) return null;
        
        return this.variantData.variants.find(v => 
          this.normalizeOptionValue(this.getVariantOption(v, 'caseSize')) === caseSize && 
          this.normalizeOptionValue(this.getVariantOption(v, 'wristSize')) === wristSize && 
          this.normalizeOptionValue(this.getVariantOption(v, 'color')) === color
        );
      }

      updateVariantImage() {
        // Minimal debounce - just prevent double-firing
        clearTimeout(this.imageUpdateDebounceTimer);
        this.imageUpdateDebounceTimer = setTimeout(() => {
          const variant = this.findVariantForImage();
          if (!variant) return;

          const imageData = variant.featured_image || variant.featured_media?.preview_image || variant.image;
          if (!imageData) return;

          this.updateProductImage(imageData);
        }, 16); // ~1 frame, just to batch rapid changes
      }

      updateProductImage(imageData) {
        if (this.isUpdatingImage) return;
        this.isUpdatingImage = true;

        // Save current scroll position to prevent page jump (especially on mobile)
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        
        // Temporarily disable smooth scrolling to prevent browser scroll behavior
        const htmlEl = document.documentElement;
        const originalScrollBehavior = htmlEl.style.scrollBehavior;
        htmlEl.style.scrollBehavior = 'auto';

        // Create a scroll lock function that we'll call multiple times
        const restoreScroll = () => {
          window.scrollTo(scrollX, scrollY);
        };

        const mainImage = this.findMainProductImage();
        
        if (mainImage && imageData) {
          const srcset = this.generateSrcset(imageData.src);
          const sizes = this.imageSizesAttr();
          // 600 IS one of the srcset widths. The old code used 500 — a width
          // srcset never offers — so every swap kicked off an extra download
          // that no preload could ever have warmed.
          const newSrc = this.getSizedImageUrl(imageData.src, 600);

          const finish = () => {
            this.classList.remove('variant-picker-custom--updating-image');
            this.isUpdatingImage = false;
            restoreScroll();
            requestAnimationFrame(() => {
              restoreScroll();
              htmlEl.style.scrollBehavior = originalScrollBehavior;
            });
          };

          // Swap only once the replacement has actually loaded. Assigning src
          // straight away leaves the <img> showing the PREVIOUS colour's frame
          // until the new one arrives and then hard-pops — that is the
          // transition the shopper sees. For a warmed image onload fires in the
          // same frame, so the fast path stays fast.
          //
          // NB do NOT gate this on img.decode(): on a detached Image carrying
          // srcset + sizes it rejects with EncodingError while the browser is
          // still settling on a candidate, which silently skips the swap
          // altogether and leaves the stale colour on screen.
          this.classList.add('variant-picker-custom--updating-image');

          const stage = new Image();
          stage.sizes = sizes;
          stage.srcset = srcset;

          stage.onload = () => {
            mainImage.srcset = srcset;
            mainImage.sizes = sizes;
            mainImage.src = newSrc;
            if (imageData.alt) mainImage.alt = imageData.alt;
            this.preloadedImages.set(imageData.src, true);
            finish();
          };

          // A load failure means a broken asset — keep the image we have rather
          // than swapping to a placeholder.
          stage.onerror = finish;

          stage.src = newSrc;
        } else {
          this.isUpdatingImage = false;
          htmlEl.style.scrollBehavior = originalScrollBehavior;
        }

        // NOTE: we deliberately do NOT call media-gallery's setActiveMedia here.
        // Dawn's setActiveMedia(mediaId, prepend) has no "don't scroll" flag - it
        // always queues a deferred window.scrollTo({behavior:'smooth'}) that yanks
        // the page up to the image whenever the image sits above the viewport
        // (i.e. always on mobile, where the gallery stacks above the picker).
        // The main <img> is swapped directly above, so the gallery call added
        // nothing anyway: it was passed an *image* id while the gallery keys on
        // *media* ids, so it never matched and always fell back to the first slide.
      }

      findMainProductImage() {
        const productInfo = this.closest('product-info');
        const productContainer = productInfo?.closest('.product') || document.querySelector('.product');
        
        const selectors = [
          '.product__media-wrapper .product__media--first img',
          '.product__media-wrapper li:first-child img',
          '.product__media-wrapper img.image-magnify-lightbox',
          '.product__media-wrapper .product__media img',
          '.product__media img',
          'media-gallery .product__media img',
          'media-gallery img',
          '.product-media-container img',
          '[data-product-media-type="image"] img',
          '.product__media-list img'
        ];

        if (productContainer) {
          for (const selector of selectors) {
            const img = productContainer.querySelector(selector);
            if (img) return img;
          }
        }

        for (const selector of selectors) {
          const img = document.querySelector(selector);
          if (img) return img;
        }

        return null;
      }

      getSizedImageUrl(src, width) {
        if (!src) return '';
        
        // Use Shopify's image_url filter pattern for optimal CDN delivery
        // Remove any existing size parameters first
        let cleanSrc = src.replace(/_\d+x\d*(@\d+x)?/g, '');
        
        const urlParts = cleanSrc.split('?');
        const baseUrl = urlParts[0];
        const extension = baseUrl.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i);
        
        if (extension) {
          // Use exact width for CDN cache hits
          const newUrl = baseUrl.replace(extension[0], `_${width}x${extension[0]}`);
          return urlParts.length > 1 ? `${newUrl}?${urlParts[1]}` : newUrl;
        }
        
        return src;
      }

      generateSrcset(src) {
        if (!src) return '';
        
        // Fewer sizes for faster parsing
        const widths = [400, 600, 800, 1200];
        return widths
          .map(width => `${this.getSizedImageUrl(src, width)} ${width}w`)
          .join(', ');
      }

      // ============================================================
      // Variant Update
      // ============================================================

      updateVariant() {
        if (!this.selections.caseSize || !this.selections.color || !this.selections.wristSize) {
          return;
        }

        const matchingVariant = this.findMatchingVariant();
        
        if (matchingVariant) {
          // Pass variant for availability checking
          this.setVariantId(matchingVariant.id, matchingVariant);
          this.publishVariantChange(matchingVariant);
          
          const imageData = matchingVariant.featured_image || matchingVariant.image;
          if (imageData) {
            this.updateProductImage(imageData);
          }
        } else {
          this.clearVariantId();
          this.handleUnavailableVariant();
        }
      }

      findMatchingVariant() {
        if (!this.variantData) return null;
        
        // Normalize selections for comparison (handle any encoding differences)
        const normalizedCaseSize = this.normalizeOptionValue(this.selections.caseSize);
        const normalizedWristSize = this.normalizeOptionValue(this.wristMatchValue(this.selections.wristSize));
        const normalizedColor = this.normalizeOptionValue(this.selections.color);
        
        return this.variantData.variants.find(variant => 
          this.normalizeOptionValue(this.getVariantOption(variant, 'caseSize')) === normalizedCaseSize &&
          this.normalizeOptionValue(this.getVariantOption(variant, 'wristSize')) === normalizedWristSize &&
          this.normalizeOptionValue(this.getVariantOption(variant, 'color')) === normalizedColor
        );
      }

      // "Wunschmaß" is a theme-only size with no variant of its own: it is sewn to measure on the
      // base variant named in data-wunschmass-fallback (the M variant), and the measurement travels
      // as a line item property. Everything that matches variants goes through this.
      wristMatchValue(value) {
        const alias = this.steps.wristSize?.querySelector('[data-wunschmass-fallback]');
        if (alias && value === alias.dataset.optionValue) return alias.dataset.wunschmassFallback;
        return value;
      }

      normalizeOptionValue(value) {
        if (!value) return '';
        // Decode HTML entities and normalize whitespace
        const textarea = document.createElement('textarea');
        textarea.innerHTML = value;
        return textarea.value.trim();
      }

      setVariantId(variantId, variant = null) {
        // Use cached reference or find it
        if (!this.formVariantInput) {
          this.cacheFormVariantInput();
        }
        
        if (this.formVariantInput) {
          this.formVariantInput.value = variantId;
          
          // Check variant availability and update disabled state
          const isAvailable = variant ? variant.available : true;
          this.formVariantInput.disabled = !isAvailable;
          
          // Dispatch change event to notify other components
          this.formVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Update add to cart button state
          this.updateAddToCartButton(variant);
        }
        
        // Update URL to reflect selected variant (debounced to avoid rapid history changes).
        // Wunschmaß rides on the M variant but is not that variant: a ?variant=<M id> link
        // would reload (or share) as a plain M band, so leave the URL without it.
        clearTimeout(this.urlUpdateTimer);
        this.urlUpdateTimer = setTimeout(() => {
          if (this.isWunschmassSelected()) this.removeVariantFromUrl();
          else this.updateUrl(variantId);
        }, 150);
        
        // Hide warning when valid selection is made
        this.hideWarning();
      }

      clearVariantId() {
        // Use cached reference or find it
        if (!this.formVariantInput) {
          this.cacheFormVariantInput();
        }
        
        if (this.formVariantInput) {
          this.formVariantInput.value = '';
          // A sold-out pick disables the input; an incomplete selection is not
          // sold out, so the form must become usable again.
          this.formVariantInput.disabled = false;
        }

        this.resetAddToCartButton();

        // Any pending ?variant= write belongs to the selection we just left.
        clearTimeout(this.urlUpdateTimer);
        this.removeVariantFromUrl();
      }

      resetAddToCartButton() {
        const submitButton = this.productFormElement?.querySelector('[type="submit"]');
        const buttonText = submitButton?.querySelector('span');
        
        if (!submitButton || !buttonText) return;
        
        submitButton.disabled = false;
        buttonText.textContent = window.variantStrings?.addToCart || 'In den Warenkorb legen';
      }

      updateAddToCartButton(variant) {
        // Update the add to cart button based on variant availability
        const submitButton = this.productFormElement?.querySelector('[type="submit"]');
        const buttonText = submitButton?.querySelector('span');
        
        if (!submitButton || !buttonText) return;
        
        if (variant && !variant.available) {
          submitButton.disabled = true;
          buttonText.textContent = window.variantStrings?.soldOut || 'Ausverkauft';
        } else if (variant) {
          submitButton.disabled = false;
          buttonText.textContent = window.variantStrings?.addToCart || 'In den Warenkorb legen';
        }
      }

      publishVariantChange(variant) {
        // Note: We intentionally do NOT publish PUB_SUB_EVENTS.optionValueSelectionChange here.
        // The custom variant picker fully manages:
        // - Form input value (setVariantId)
        // - URL updates (updateUrl)
        // - Add-to-cart button state (updateAddToCartButton)
        // - Image updates (updateProductImage)
        // 
        // Publishing the event would cause product-info.js to make a server request
        // and override our correct state with potentially wrong data, because:
        // 1. product-info.js expects selectedOptionValues but we use a different structure
        // 2. product-info.js looks for variant-selects element which we don't have
        //
        // Instead, we publish variantChange for other components that might need it
        // (like price updates, inventory display, etc.) but with the variant already resolved.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.dataset.section,
              variant: variant,
              html: null // No HTML to parse, we handle updates directly
            }
          });
        }
      }

      updateUrl(variantId) {
        if (!variantId) return;
        
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      removeVariantFromUrl() {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('variant')) return;
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', url.toString());
      }

      handleUnavailableVariant() {
        // Publish variantChange with null variant to notify other components
        // that no valid variant is selected (e.g., for price/inventory display)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.dataset.section,
              variant: null,
              html: null
            }
          });
        }
      }

      updateScrollIndicators() {
        if (!this.steps.wristSize) return;
        
        const scrollContainer = this.steps.wristSize.querySelector('.variant-picker-step__scroll-container');
        const fadeRight = this.steps.wristSize.querySelector('.variant-picker-step__scroll-fade--right');
        
        if (!scrollContainer || !fadeRight) return;
        
        const isScrollable = scrollContainer.scrollWidth > scrollContainer.clientWidth;
        const isAtEnd = scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth - 5;
        
        fadeRight.classList.toggle('variant-picker-step__scroll-fade--hidden', !isScrollable || isAtEnd);
      }

      getSelections() {
        return { ...this.selections };
      }

      getSelectedVariant() {
        return this.findMatchingVariant();
      }
    }
  );
}
